import type { JSONContent } from '@tiptap/react';
import type { WorkflowType } from '@/types';
import type { Channel } from '@/modules/workflows/types';

// Exported vectors, used as-is and never re-drawn.
import headerBack from '@/assets/protocols/header-back.svg';
import headerEllipsis from '@/assets/protocols/header-ellipsis.svg';
import headerPause from '@/assets/protocols/header-pause.svg';
import headerPlay from '@/assets/protocols/header-play.svg';
import headerPlayDisabled from '@/assets/protocols/header-play-disabled.svg';
import panelList from '@/assets/protocols/panel-list.svg';
import panelPlus from '@/assets/protocols/panel-plus.svg';
import panelToggle from '@/assets/protocols/panel-toggle.svg';
import accordionChevron from '@/assets/protocols/accordion-chevron.svg';
import chevronDown from '@/assets/protocols/chevron-down.svg';
import rowGrip from '@/assets/protocols/row-grip.svg';
import rowPlus from '@/assets/protocols/row-plus.svg';
import rowEllipsis from '@/assets/protocols/row-ellipsis.svg';
import gripVertical from '@/assets/protocols/grip-vertical.svg';
import menuCheckIcon from '@/assets/protocols/menu-check.svg';
import chevronDownFilled from '@/assets/protocols/chevron-down-filled.svg';
import chevronUpSm from '@/assets/protocols/chevron-up-sm.svg';
import branchIf from '@/assets/protocols/branch-if.svg';
import branchElse from '@/assets/protocols/branch-else.svg';
import chipHandoff from '@/assets/protocols/chip-handoff.svg';
import chipListVideo from '@/assets/protocols/chip-listvideo.svg';
import chipLink from '@/assets/protocols/chip-link.svg';

export const protocolIcons = {
  headerBack,
  headerEllipsis,
  headerPause,
  headerPlay,
  headerPlayDisabled,
  panelList,
  panelPlus,
  panelToggle,
  accordionChevron,
  chevronDown,
  chevronDownFilled,
  chevronUpSm,
  rowGrip,
  rowPlus,
  rowEllipsis,
  gripVertical,
  branchIf,
  branchElse,
  chipHandoff,
  chipListVideo,
  chipLink
};

export const menuCheck = menuCheckIcon;

/* 14.25px glyphs for the "/" menu's tinted Icon-Filler chip. */
import menuCondition from '@/assets/protocols/menu-condition.svg';
import menuSubprotocol from '@/assets/protocols/menu-subprotocol.svg';
import menuLink from '@/assets/protocols/menu-link.svg';
import menuUnassign from '@/assets/protocols/menu-unassign.svg';
import menuHandoff from '@/assets/protocols/menu-handoff.svg';
import menuSnooze from '@/assets/protocols/menu-snooze.svg';
import menuPending from '@/assets/protocols/menu-pending.svg';
import menuClose from '@/assets/protocols/menu-close.svg';
import menuReopen from '@/assets/protocols/menu-reopen.svg';
import menuEnd from '@/assets/protocols/menu-end.svg';
import menuTag from '@/assets/protocols/menu-tag.svg';
import menuAttributes from '@/assets/protocols/menu-attributes.svg';
import menuNote from '@/assets/protocols/menu-note.svg';
import menuMail from '@/assets/protocols/menu-mail.svg';
import menuShopify from '@/assets/protocols/menu-shopify.svg';
import menuTrash from '@/assets/protocols/menu-trash.svg';
import menuPlus from '@/assets/protocols/menu-plus.svg';
import menuWait from '@/assets/protocols/menu-wait.svg';

export const menuIcons = {
  condition: menuCondition,
  subprotocol: menuSubprotocol,
  link: menuLink,
  unassign: menuUnassign,
  handoff: menuHandoff,
  snooze: menuSnooze,
  pending: menuPending,
  close: menuClose,
  reopen: menuReopen,
  end: menuEnd,
  tag: menuTag,
  attributes: menuAttributes,
  note: menuNote,
  mail: menuMail,
  shopify: menuShopify,
  trash: menuTrash,
  plus: menuPlus,
  // Wait's hourglass is not from the design export; drawn to match the set until one is.
  wait: menuWait
};

/* 20px glyphs for the Ticket Actions panel's 32px Input-Chip. */
import actionTag from '@/assets/protocols/action-tag.svg';
import actionAttributes from '@/assets/protocols/action-attributes.svg';
import actionNote from '@/assets/protocols/action-note.svg';
import actionEmail from '@/assets/protocols/action-email.svg';
import actionReopen from '@/assets/protocols/action-reopen.svg';
import actionSnooze from '@/assets/protocols/action-snooze.svg';
import actionPending from '@/assets/protocols/action-pending.svg';
import actionCloseTicket from '@/assets/protocols/action-close-ticket.svg';
import actionUnassign from '@/assets/protocols/action-unassign.svg';
import actionClose from '@/assets/protocols/action-close.svg';

export const actionIcons = {
  tag: actionTag,
  attributes: actionAttributes,
  note: actionNote,
  email: actionEmail,
  reopen: actionReopen,
  snooze: actionSnooze,
  pending: actionPending,
  close: actionCloseTicket,
  unassign: actionUnassign,
  end: actionClose
};

/* Send Email composer. */
import modalClose from '@/assets/protocols/modal-close.svg';
import rteBold from '@/assets/protocols/rte-bold.svg';
import rteItalic from '@/assets/protocols/rte-italic.svg';
import rtePaintBucket from '@/assets/protocols/rte-paint-bucket.svg';
import rteList from '@/assets/protocols/rte-list.svg';
import rteListOrdered from '@/assets/protocols/rte-list-ordered.svg';
import rteUndo from '@/assets/protocols/rte-undo.svg';
import rteRedo from '@/assets/protocols/rte-redo.svg';
import rteListFilter from '@/assets/protocols/rte-list-filter.svg';
import rtePlus from '@/assets/protocols/rte-plus.svg';
import rteBaseline from '@/assets/protocols/rte-baseline.svg';
import rteSmile from '@/assets/protocols/rte-smile.svg';
import rteLink from '@/assets/protocols/rte-link.svg';
import rteSignature from '@/assets/protocols/rte-signature.svg';
import rteMic from '@/assets/protocols/rte-mic.svg';

export const rteIcons = {
  close: modalClose,
  bold: rteBold,
  italic: rteItalic,
  paintBucket: rtePaintBucket,
  list: rteList,
  listOrdered: rteListOrdered,
  undo: rteUndo,
  redo: rteRedo,
  refine: rteListFilter,
  plus: rtePlus,
  baseline: rteBaseline,
  smile: rteSmile,
  link: rteLink,
  signature: rteSignature,
  mic: rteMic
};

/* Filled 16px listing glyphs; the off variant is the same shape flooded #DADCE0. */
import channelEmail from '@/assets/protocols/channel-email.svg';
import channelEmailOff from '@/assets/protocols/channel-email-off.svg';
import channelChat from '@/assets/protocols/channel-chat.svg';
import channelChatOff from '@/assets/protocols/channel-chat-off.svg';
import channelSocial from '@/assets/protocols/channel-social.svg';
import channelSocialOff from '@/assets/protocols/channel-social-off.svg';
import channelSms from '@/assets/protocols/channel-sms.svg';
import channelSmsOff from '@/assets/protocols/channel-sms-off.svg';

export const channelIcons: Record<Channel, { on: string; off: string }> = {
  email: { on: channelEmail, off: channelEmailOff },
  chat: { on: channelChat, off: channelChatOff },
  social: { on: channelSocial, off: channelSocialOff },
  sms: { on: channelSms, off: channelSmsOff }
};

/** Channel order as drawn on the listing page. Matched to channels by name. */
export const CHANNEL_OPTIONS: { id: Channel; label: string }[] = [
  { id: 'email', label: 'Email' },
  { id: 'sms', label: 'SMS' },
  { id: 'social', label: 'Social' },
  { id: 'chat', label: 'Chat' }
];

/** `workflow.description` is what vector sync embeds for retrieval — it caps here. */
export const DESCRIPTION_MAX_LENGTH = 500;

/** Placeholder copy, verbatim from the design. */
export const PLACEHOLDERS = {
  name: 'Untitled protocol',
  whenToUse: 'Tell kim AI when to use this protocol',
  preCheck: 'Write what Kim AI needs to verify before executing the protocol',
  instructions: 'Start typing instructions, use / to search and find tools',
  condition: 'Describe the condition',
  branchStep: 'Add a step',
  subName: 'Untitled Sub Protocol'
} as const;

export const EMPTY_SUB_PROTOCOLS = 'No sub protocols';

// Stored as workflows of this type; the backend must add 'protocol' to the workflow type enum.
export const PROTOCOL_WORKFLOW_TYPE: WorkflowType = 'protocol';

/** Fixed headings a fresh section opens with; the author can rename them in place. */
export const SECTION_KINDS = ['preCheck', 'instructions', 'subProtocol'] as const;

export const SECTION_TITLES = { preCheck: 'Pre-check', instructions: 'Instructions', subProtocol: 'Untitled Sub Protocol' } as const;

// Node types the editor may write; anything else is dropped before save.
export const PROTOCOL_NODE_TYPES = new Set([
  'doc',
  'paragraph',
  'text',
  'heading',
  'bulletList',
  'orderedList',
  'listItem',
  'blockquote',
  'codeBlock',
  'horizontalRule',
  'bold',
  'italic',
  'strike',
  'hardBreak',
  'actionChip',
  'escalateChip',
  'waitChip',
  'knowledgeBaseChip',
  'workflowLinkChip',
  'replyBlock',
  'conditionBlock',
  'ifBlock',
  'elseIfBlock',
  'elseBlock',
  'conditionContent',
  'preCheck',
  'instructions',
  'subProtocol'
]);

// Empty doc: each section carries a paragraph, or ProseMirror has nowhere to put a caret.
export const createEmptyDoc = (): JSONContent => ({
  type: 'doc',
  content: [
    { type: 'preCheck', attrs: { title: SECTION_TITLES.preCheck }, content: [{ type: 'paragraph' }] },
    { type: 'instructions', attrs: { title: SECTION_TITLES.instructions }, content: [{ type: 'paragraph' }] }
  ]
});

// Ticket actions ride on actionChip, namespaced kim-; each id needs a backend tool.
export const KIM_PROVIDER = 'Kim';
export const KIM_ACTION_PREFIX = 'kim-';
export const SUB_PROTOCOL_ACTION_ID = 'kim-run_sub_protocol';

/** Ticket actions that are not escalations, in "/" menu order. */
export type TicketActionId = 'unassign' | 'snooze' | 'pending' | 'reopen' | 'close' | 'end' | 'tag' | 'attributes';
/**
 * Escalations have first-class `escalateChip` types, so they are encoded differently. The escalation
 * Unassign is `escalation-unassign`: plain `unassign` is the ticket action above.
 */
export type EscalationActionId = 'assign' | 'escalation-unassign' | 'note' | 'email' | 'slack';

/** The "/" menu's Escalation group, in order. Each one falls back to the brand's escalation policy. */
export const ESCALATION_ACTION_IDS: EscalationActionId[] = ['email', 'assign', 'escalation-unassign', 'note', 'slack'];

// The Slack mark cropped to its glyph; the brand logo's padding shrinks it to a dot at 14px.
import SLACK_ICON from '@/assets/protocols/menu-slack.svg';
export type ProtocolActionId = TicketActionId | EscalationActionId;

export type ActionTone = 'warning' | 'error';

// menuLabel is the "/" menu's wording, panelLabel the panel's.
export const ACTION_CATALOG: {
  id: ProtocolActionId;
  menuLabel: string;
  panelLabel: string;
  icon: string;
  chipIcon: string;
  tone: ActionTone;
}[] = [
  { id: 'tag', menuLabel: 'Add Tags', panelLabel: 'Add tags', icon: actionIcons.tag, chipIcon: menuIcons.tag, tone: 'warning' },
  {
    id: 'attributes',
    menuLabel: 'Set Ticket Fields',
    panelLabel: 'Set ticket fields',
    icon: actionIcons.attributes,
    chipIcon: menuIcons.attributes,
    tone: 'warning'
  },
  // Escalations read red, apart from the ticket actions around them.
  {
    id: 'note',
    menuLabel: 'Send Private Note',
    panelLabel: 'Send private note',
    icon: actionIcons.note,
    chipIcon: menuIcons.note,
    tone: 'error'
  },
  { id: 'email', menuLabel: 'Send Email', panelLabel: 'Send Email', icon: actionIcons.email, chipIcon: menuIcons.mail, tone: 'error' },
  {
    id: 'slack',
    menuLabel: 'Send Slack Message',
    panelLabel: 'Send Slack message',
    icon: SLACK_ICON,
    chipIcon: SLACK_ICON,
    tone: 'error'
  },
  {
    id: 'reopen',
    menuLabel: 'Re-open Ticket',
    panelLabel: 'Re-open Ticket',
    icon: actionIcons.reopen,
    chipIcon: menuIcons.reopen,
    tone: 'error'
  },
  { id: 'snooze', menuLabel: 'Snooze Ticket', panelLabel: 'Snooze ticket', icon: actionIcons.snooze, chipIcon: menuIcons.snooze, tone: 'error' },
  // Pending's hourglass is not from the design export; drawn to match the set until one is.
  {
    id: 'pending',
    menuLabel: 'Mark as Pending',
    panelLabel: 'Mark as pending',
    icon: actionIcons.pending,
    chipIcon: menuIcons.pending,
    tone: 'error'
  },
  {
    id: 'unassign',
    menuLabel: 'Un-Assign Ticket',
    panelLabel: 'Un-assign ticket',
    icon: actionIcons.unassign,
    chipIcon: menuIcons.unassign,
    tone: 'error'
  },
  // Close's circle-check is not from the design export; drawn to match the set until one is.
  { id: 'close', menuLabel: 'Close Ticket', panelLabel: 'Close Ticket', icon: actionIcons.close, chipIcon: menuIcons.close, tone: 'error' },
  // End only marks where a flow stops; it closes nothing.
  { id: 'end', menuLabel: 'End', panelLabel: 'End', icon: actionIcons.end, chipIcon: menuIcons.end, tone: 'error' },
  {
    id: 'assign',
    menuLabel: 'Assign',
    panelLabel: 'Assign',
    icon: actionIcons.unassign,
    chipIcon: menuIcons.handoff,
    tone: 'error'
  },
  {
    id: 'escalation-unassign',
    menuLabel: 'Unassign',
    panelLabel: 'Unassign',
    icon: actionIcons.unassign,
    chipIcon: menuIcons.unassign,
    tone: 'error'
  }
];

export const actionMeta = (id: ProtocolActionId) => ACTION_CATALOG.find((entry) => entry.id === id);

/**
 * Ticket actions that run as post-processing tools: after the reply lands, never mid-flow.
 * A row shows in the "/" menu only when the account's helpdesk offers its tool flagged `postProcessing`.
 */
export const POST_PROCESSING_ACTIONS: Partial<
  Record<TicketActionId, { tool: string; staticInputs?: Record<string, unknown> }>
> = {
  tag: { tool: 'tickets_tag_add' },
  snooze: { tool: 'tickets_snooze' },
  pending: { tool: 'tickets_status_set', staticInputs: { status: 'pending' } },
  reopen: { tool: 'tickets_status_set', staticInputs: { status: 'open' } },
  close: { tool: 'tickets_close' },
  // The helpdesk's own ticket fields (Gorgias), e.g. the required ones it checks before a close.
  attributes: { tool: 'tickets_fields_set' }
};

// The Ticket Actions panel's own node omits Assign and Unassign, though the "/" menu lists them.
export const PANEL_ACTION_IDS: ProtocolActionId[] = ['tag', 'attributes', 'note', 'email', 'slack', 'reopen', 'snooze', 'pending', 'unassign', 'close', 'end'];

/** The design's Title/Text placeholders, shown for a tool with no schema. */
export const DEFAULT_TOOL_FIELDS = [
  { title: 'Title', text: 'Text' },
  { title: 'Title', text: 'Text' },
  { title: 'Title', text: 'Text' }
];

export const ACTION_CONFIGURE_WARNING = 'Toggling this tool call ticket action will affect all instances within this protocol.';

// Menu lists size to their content, between a floor and a ceiling.
export const MENU_LIST_STYLE = { width: 'max-content', minWidth: 160, maxWidth: 320 } as const;
